import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { EventLevel, RunEvent } from '../api/types';
import { levelPresentation } from '../components/presentation';
import { formatTime } from '../lib/format';
import { strings } from '../strings';

const LEVELS: EventLevel[] = ['debug', 'info', 'warning', 'error'];

/**
 * The event stream: filter by level and resource; auto-scroll pauses when the user scrolls up (spec 6.5).
 * `fill` lets the log fill its container (the Activity window) instead of a fixed height.
 */
export function EventLogView({ events, fill = false }: { events: RunEvent[]; fill?: boolean }) {
  const levelId = useId();
  const resourceId = useId();
  const [level, setLevel] = useState<EventLevel | ''>('');
  const [resource, setResource] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const container = useRef<HTMLDivElement>(null);

  const resources = useMemo(
    () => [...new Set(events.map((e) => e.resourceId).filter((id): id is string => !!id))].sort(),
    [events],
  );
  const visible = useMemo(
    () => events.filter((e) => (!level || e.level === level) && (!resource || e.resourceId === resource)),
    [events, level, resource],
  );

  useEffect(() => {
    const element = container.current;
    if (autoScroll && element) element.scrollTop = element.scrollHeight;
  }, [visible, autoScroll]);

  return (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col' : undefined}>
      <div className="mb-2 flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor={levelId}>
            {strings.runs.level}
          </label>
          <select
            id={levelId}
            className="input w-40"
            value={level}
            onChange={(e) => setLevel(e.target.value as EventLevel | '')}
          >
            <option value="">{strings.runs.allLevels}</option>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {strings.status.level[l]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor={resourceId}>
            {strings.runs.filterResource}
          </label>
          <select
            id={resourceId}
            className="input w-48"
            value={resource}
            onChange={(e) => setResource(e.target.value)}
          >
            <option value="">{strings.runs.allResources}</option>
            {resources.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </div>
        {!autoScroll && (
          <div className="flex items-center gap-2 text-sm" role="status">
            {strings.runs.autoScrollPaused}
            <button type="button" className="btn" onClick={() => setAutoScroll(true)}>
              {strings.runs.resumeAutoScroll}
            </button>
          </div>
        )}
      </div>
      <div
        ref={container}
        tabIndex={0}
        role="log"
        aria-label={strings.runs.log}
        className={`${fill ? 'min-h-0 flex-1' : 'max-h-96'} overflow-y-auto rounded-md border border-slate-300 bg-slate-50 p-2 font-mono text-xs dark:border-slate-600 dark:bg-slate-900`}
        onScroll={(event) => {
          const element = event.currentTarget;
          const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 8;
          if (atBottom !== autoScroll) setAutoScroll(atBottom);
        }}
      >
        {visible.length === 0 ? (
          <p className="font-sans text-slate-600 dark:text-slate-300">{strings.runs.noEvents}</p>
        ) : (
          <ol>
            {visible.map((event) => {
              const presentation = levelPresentation[event.level];
              const Icon = presentation.icon;
              return (
                <li key={event.sequence} className="flex gap-2 py-0.5">
                  <time dateTime={event.timestamp} className="shrink-0 text-slate-600 dark:text-slate-300">
                    {formatTime(event.timestamp)}
                  </time>
                  <span className={`badge tone-${presentation.tone} shrink-0`}>
                    <Icon aria-hidden className="size-3" />
                    {presentation.label}
                  </span>
                  {event.resourceId && <span className="shrink-0 font-semibold">{event.resourceId}</span>}
                  <span className="min-w-0 break-words">{event.message}</span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
